/**
 * Visa API Utilities
 * Centralized functions for fetching visa data from the backend
 */

import { API_ENDPOINTS } from '../config/api';
import type {
  Country,
  VisaType,
  VisaTypeFull,
  CountryWithVisas,
  GlobalStats,
  SearchParams,
  SearchResult,
} from '../types/visa';

/**
 * Fetch all countries
 */
export async function fetchCountries(): Promise<Country[]> {
  const response = await fetch(API_ENDPOINTS.countries);
  if (!response.ok) {
    throw new Error(`Failed to fetch countries: ${response.statusText}`);
  }
  const json = await response.json();
  return json.data || json;
}

/**
 * Fetch all visa types for a specific country
 */
export async function fetchVisasByCountry(iso2: string): Promise<VisaType[]> {
  const response = await fetch(API_ENDPOINTS.visa.byCountry(iso2));
  if (!response.ok) {
    throw new Error(`Failed to fetch visas for ${iso2}: ${response.statusText}`);
  }
  const json = await response.json();
  return json.data || json;
}

/**
 * Fetch basic visa details
 */
export async function fetchVisaDetails(id: string): Promise<VisaType> {
  const response = await fetch(API_ENDPOINTS.visa.details(id));
  if (!response.ok) {
    throw new Error(`Failed to fetch visa ${id}: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Fetch complete visa details with all nested relations
 */
export async function fetchVisaFull(id: string): Promise<VisaTypeFull> {
  const response = await fetch(API_ENDPOINTS.visa.full(id));
  if (!response.ok) {
    throw new Error(`Failed to fetch full visa ${id}: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Search/filter visas
 */
export async function searchVisas(params: SearchParams): Promise<SearchResult> {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      searchParams.append(key, String(value));
    }
  });
  
  const response = await fetch(`${API_ENDPOINTS.visa.search}?${searchParams.toString()}`);
  if (!response.ok) {
    throw new Error(`Failed to search visas: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Fetch complete country data with all visas
 */
export async function fetchCountryComplete(iso2: string): Promise<CountryWithVisas> {
  const response = await fetch(API_ENDPOINTS.countryDetails.complete(iso2));
  if (!response.ok) {
    throw new Error(`Failed to fetch country ${iso2}: ${response.statusText}`);
  }
  const json = await response.json();
  return json.data || json;
}

/**
 * Fetch official sources for a country
 */
export async function fetchCountrySources(iso2: string): Promise<any> {
  const response = await fetch(API_ENDPOINTS.countryDetails.sources(iso2));
  if (!response.ok) {
    throw new Error(`Failed to fetch sources for ${iso2}: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Fetch global statistics
 */
export async function fetchGlobalStats(): Promise<GlobalStats> {
  const response = await fetch(API_ENDPOINTS.stats.global);
  if (!response.ok) {
    throw new Error(`Failed to fetch global stats: ${response.statusText}`);
  }
  const json = await response.json();
  return json.data || json;
}

/**
 * Fetch visa categories list
 */
export async function fetchCategories(): Promise<Array<{ category: string; count: number }>> {
  const response = await fetch(API_ENDPOINTS.visa.categories);
  if (!response.ok) {
    throw new Error(`Failed to fetch categories: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Compare multiple visas
 */
export async function compareVisas(visaIds: string[]): Promise<{ visas: VisaType[] }> {
  const response = await fetch(API_ENDPOINTS.visa.compare, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ visaIds }),
  });
  if (!response.ok) {
    throw new Error(`Failed to compare visas: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Format currency amount
 */
export function formatCurrency(amount: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency || 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
}

/**
 * Format duration in days
 */
export function formatDuration(days?: number): string {
  if (!days) return 'N/A';
  if (days < 30) return `${days} days`;
  if (days < 365) return `${Math.round(days / 30)} months`;
  return `${Math.round(days / 365)} years`;
}

/**
 * Get visa category display name
 */
export function getCategoryDisplayName(category: string): string {
  const displayNames: Record<string, string> = {
    tourist: 'Tourist',
    business: 'Business',
    work: 'Work',
    student: 'Student',
    family: 'Family',
    transit: 'Transit',
    medical: 'Medical',
    culture: 'Culture/Sports',
    diplomatic: 'Diplomatic',
    official: 'Official',
    other: 'Other',
  };
  return displayNames[category] || category;
}

/**
 * Get visa status badge color
 */
export function getVisaStatusColor(visa: VisaType): string {
  if (!visa.isActive) return 'gray';
  if (visa.online) return 'green';
  return 'blue';
}
